import React, { useContext, useEffect, useMemo, useState } from 'react'
import { animated, useSpring } from "@react-spring/web";
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { closestCenter, DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { fixCursorSnapOffset } from "src/utils/dnd";
import { restrictToWindowEdges, snapCenterToCursor } from '@dnd-kit/modifiers';


import { DealRoomContext } from '../../components/context';

import { AssetCatergory, assetsInitialState, DealRoom, DealRoomAsset, DealRoomEditState } from 'src/types/dealRoom';
import { AssetsLayoutEnum, MenuState, SectionState } from '../../types';

import { TabBar } from 'src/components/tabbar';
import { AddSectionDialog, DraggingSectionCard, SectionBar, SectionContainer } from '../../components/sections';
import { DraggableAssetsLayout, StaticAssetsLayout } from '../../components/assetsLayout';
import { DraggingAssetCard } from '../../components/assetCard';

import _, { get } from 'lodash';
import cx from 'classnames'
import styles from './styles.module.scss';
import Actions from 'src/components/actionbar';
import { AddAssetsDialog } from 'src/components/asset/add';
import { Asset, AssetUpload } from 'src/types/assets';
import { Collection } from 'src/types/collections';
import { startBackgroundUploadTask } from 'src/redux/actions/backgroundTask';

import { addAssetToDealRoom, fetchDealRoomAssets, fetchDealRoomAssetsForSection, moveAsset, moveAssetToSection, moveSection, rearrageDealRoomAssets, removeAssetFromDealRoom } from 'src/redux/actions/dealRoom';
import Tooltip from 'src/components/tooltip';
import { useDealRoomAdoption } from 'src/redux/actions/adoption/dealRoom';
import { DEAL_ROOM } from 'src/constants/adoption/sourceTypes';

const AssetsPage: React.FC = () => {
  const dispatch = useDispatch();

  const { trackDealRoomAssetSectionViewed } = useDealRoomAdoption();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || "";

  const { id, type, sectionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const loading = useSelector((state: any) => _.get(state, `dealRooms.dealRoom['${id}'].loading`, false));
  const { dealRoom, onMenuChange, menuState, activeAssetLayout, onActiveAssetLayoutChange } = useContext(DealRoomContext);
  const dealRoomAssets = useSelector((state: any) => _.get(state, `dealRooms.dealRoom['${dealRoom.id}'].assets`, {}));
  const [activeTab, setActiveTab] = React.useState(1)
  const account = useSelector((state) => _.get(state, 'accounts.item', {}));

  const assetsForType = _.get(dealRoomAssets, _.upperCase(type), {});
  const editing = menuState.primaryAction === 'edit';

  const allAssets = dealRoomAssets[AssetCatergory.ALL] || {};
  const { loading: allAssetsLoading, items: allAssetsItems, pagination: allAssetsPagination } = allAssets;
  const externalAssets = dealRoomAssets[AssetCatergory.EXTERNAL] || {};
  const { loading: externalAssetsLoading, items: externalAssetsItems, pagination: externalAssetsPagination } = externalAssets;
  const internalAssets = dealRoomAssets[AssetCatergory.INTERNAL] || {};
  const { loading: internalAssetsLoading } = internalAssets


  useEffect(() => {
    if (id && !assetsForType.loading && _.size(assetsForType.items) === 0) {
      fetchAssets(_.upperCase(type) as AssetCatergory, 0);
    }
    if (type === "internal" || _.includes(location.pathname, "internal/sections")) {
      setActiveTab(1);
    } else if (type === "external") {
      setActiveTab(2);
    } else {
      setActiveTab(0);
    }
  }, [type])

  useEffect(() => {
    if (editing) {
      setActiveTab(1);

      const sectionsCount = _.size(dealRoom.sections);
      if (sectionsCount > 0) {
        navigate(`/deal-room/${id}/assets/internal/sections/${sectionId ?? 0}`)
        return;
      }
      navigate(`/deal-room/${id}/assets/internal`);
    }
  }, [editing]);

  useEffect(() => {
    const validSource = source?.length > 0 ? source : DEAL_ROOM;
    trackDealRoomAssetSectionViewed({ source_cta: validSource });
  }, []);

  const handleTabClick = (tab) => {

    setActiveTab(tab);
    if (tab === 1) {
      const sectionsCount = _.size(dealRoom.sections);
      if (sectionsCount > 0 && !_.includes(location.pathname, "internal/sections")) {
        navigate(`/deal-room/${id}/assets/internal/sections/0`)
        return;
      }
      navigate(`/deal-room/${id}/assets/internal`)
    }
    else if (tab === 2) {
      navigate(`/deal-room/${id}/assets/external`)
    }
    else {
      navigate(`/deal-room/${id}/assets/all`)
    }
  }

  const fetchAssets = (assetCategory: AssetCatergory, page: number,) => {

    if (!assetCategory) return;
    dispatch(fetchDealRoomAssets({
      id: id ?? "",
      page: page,
      limit: 20,
      assetCategory: assetCategory
    }))
  }

  return (
    <div className={styles.assetsContainer}>

      <TabBar
        className={cx(styles.tabs)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={handleTabClick}
      >
        <div className={cx(styles.tab, {
          [styles.active]: activeTab == 0,
        })}>
          All
        </div>
        <div className={cx(styles.tab, styles.truncatedText, { [styles.active]: activeTab == 1 })}>
          {_.get(account, "name", "")}
        </div>
        <div
          className={cx(styles.tab, styles.truncatedText, {
            [styles.active]: activeTab == 2,
          })}
        >
          {_.size(_.get(dealRoom, "customerName")) === 0 ? "Buyer name" : dealRoom.customerName}
          <Tooltip content={'You can change the buyer name on the banner'} placement='right'>
            <i className={cx("paperflite-info", styles.infoIcon)}></i>
          </Tooltip>
        </div>
      </TabBar>

      <div className={cx(styles.stacks, styles.tabContainer)}>
        <TabSection index={0} selected={activeTab}>
          <StaticAssetsRenderer
            fetchAssetsForPage={(page) => {
              fetchAssets(AssetCatergory.ALL, page)
            }}
            canAddAsset={get(dealRoom, 'canEdit', false)}
            header={`Assets uploaded by all ${!editing ? `(${_.get(dealRoom, 'allAssetsCount', 0)})` : ""}`}
            subText="View files from both your team and buyers in one place."
            assets={allAssets} loading={allAssetsLoading} visible={activeTab === 0} activeAssetLayout={activeAssetLayout} />
        </TabSection>
        <TabSection index={1} selected={activeTab}>
          <InternalAssetsRenderer assetsLoading={internalAssetsLoading} visible={activeTab === 1} activeAssetLayout={activeAssetLayout}
            fetchAssetsForPage={(page) => {
              fetchAssets(AssetCatergory.INTERNAL, page)
            }} />
        </TabSection>
        <TabSection index={2} selected={activeTab}>
          <StaticAssetsRenderer
            fetchAssetsForPage={(page) => {
              fetchAssets(AssetCatergory.EXTERNAL, page)
            }}
            canAddAsset={false}
            header={`Buyer documents ${!editing ? `(${_.get(dealRoom, 'externalAssetsCount', 0)})` : ""}`}
            subText="View files uploaded by your buyers."
            assets={externalAssets} loading={externalAssetsLoading} visible={activeTab === 2} activeAssetLayout={activeAssetLayout} />
        </TabSection>

      </div>

      <div className={styles.viewerMenu}>

        <Actions.Container>

          <Actions.Row>

            {activeTab === 1 && editing && <Actions.IconButton
              icon="paperflite-add-category"
              tooltip="Add Section"
              onClick={() => {
                onMenuChange({
                  ...menuState,
                  primaryAction: 'edit',
                  secondaryAction: 'add-section'
                });
              }}
            />}

            {activeTab === 1 && editing && <Actions.Button
              width={3}
              icon="paperflite-add"
              title="Add Asset"
              onClick={() => {
                onMenuChange({
                  ...menuState,
                  primaryAction: 'edit',
                  secondaryAction: 'add-asset'
                });
              }}
            />}

            <Actions.Set>

              <Actions.SetItem
                className={cx({ [styles.active]: activeAssetLayout === AssetsLayoutEnum.GRID })}
                icon="paperflite-categories"
                tooltip="Card"
                onClick={() => onActiveAssetLayoutChange(AssetsLayoutEnum.GRID)}
              />
              <Actions.SetItem
                className={cx({ [styles.active]: activeAssetLayout === AssetsLayoutEnum.LIST })}
                icon="paperflite-list"
                tooltip="List"
                onClick={() => onActiveAssetLayoutChange(AssetsLayoutEnum.LIST)}
              />
            </Actions.Set>
          </Actions.Row>
        </Actions.Container>
      </div>

    </div>
  )
}

interface DraggingAssetState {
  asset: DealRoomAsset;
  overSection: string | null;
}

interface InternalAssetsRendererProps {
  activeAssetLayout: AssetsLayoutEnum;
  visible: boolean;
  assetsLoading: boolean;
  fetchAssetsForPage: (page: number) => void;
}

const InternalAssetsRenderer: React.FC<InternalAssetsRendererProps> = ({ activeAssetLayout, visible, assetsLoading, fetchAssetsForPage }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id, sectionId } = useParams();

  const { dealRoom, dealRoomAssets, onMenuChange, menuState, isDealRoomLoading: loading } = useContext(DealRoomContext);
  const editing = menuState.primaryAction === 'edit';

  const [draggingSection, setDraggingSection] = useState<SectionState | null>(null);
  const [draggingAsset, setDraggingAsset] = useState<DraggingAssetState | null>(null);

  const sectionItems = useMemo(() => {
    if (!dealRoom.id || !dealRoom.sections) {
      return [];
    }

    const items = dealRoom.sections.map((item, index) => {
      const data = {
        uid: item.uid,
        id: item.item?.id,
        index: index,
        name: item.item?.name,
        assets: item.item?.assets,
        loading: item.loading,
        loaded: item.loaded,
        active: Number(sectionId) === index,
      } as SectionState;

      return data;
    });

    return items;
  }, [dealRoom, sectionId]);


  useEffect(() => {
    const activeIndex = parseInt(sectionId || '0');
    if (dealRoom.id && activeIndex >= 0) {
      const section = sectionItems[activeIndex];
      if (section && !section.loaded && section.id) {

        dispatch(fetchDealRoomAssetsForSection({
          id: id!,
          page: 0,
          limit: 20,
          assetCategory: AssetCatergory.INTERNAL,
          sectionId: section.id
        }))
      }
    }
  }, [sectionId, dealRoom.id, _.size(sectionItems)]);


  useEffect(() => {
    const activeIndex = parseInt(sectionId || '0');
    const sectionsCount = _.size(sectionItems);
    if (sectionsCount > 0 && activeIndex >= sectionsCount - 1) {
      handleSectionClick(sectionsCount - 1);
    }
  }, [sectionId, _.size(sectionItems)]);


  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });

  const sensors = useSensors(
    pointerSensor
  );

  const handleSectionClick = (index) => {
    navigate(`/deal-room/${id}/assets/internal/sections/${index}`);
  }

  const handleAssetClick = (asset: DealRoomAsset) => {
    handleAssetClickInSection(null, asset)
  }

  const handleAssetClickInSection = (section: SectionState | null, asset: DealRoomAsset) => {
    const sectionIndex = section ? section.index : -1;
    let source = `deal-room/${dealRoom.id}/assets/internal`;
    if (sectionIndex >= 0) {
      source = `${source}/sections/${sectionIndex}`;
    }

    const url = `/assets/${asset.asset.id}?source=${source}`;
    navigate(url);
  }

  const handleSectionMove = (from: number, to: number) => {
    const section = sectionItems[from];
    if (!section) {
      return;
    }

    let newActiveIndex = -1;
    const activeSection = sectionItems.find(s => s.active)?.index || 0;
    if (to <= activeSection && from > activeSection) {
      newActiveIndex = activeSection + 1;
    } else if (from === activeSection) {
      newActiveIndex = to;
    } else if (from < activeSection && to >= activeSection) {
      newActiveIndex = activeSection - 1;
    }

    sectionMoveHandler(from, to);

    if (newActiveIndex >= 0) {
      handleSectionClick(newActiveIndex);
    }
  }

  const sectionMoveHandler = (from: number, to: number) => {
    dispatch(moveSection({
      dealRoom: dealRoom.id!,
      from: from,
      to: to
    }));
  }

  const handleAssetDragStart = (event) => {
    const asset = event.active.data.current.asset;
    const section = event.active.data.current.section;

    if (asset) {
      setDraggingAsset({
        asset: asset,
        overSection: null
      });
    } else if (section) {
      setDraggingSection(section);
    }
  }

  const handleAssetDragMove = (event) => {
    const sectionId = _.get(event.over, 'data.current.section.id', null);
    setDraggingAsset((prev) => {
      if (!prev) {
        return null;
      }

      return {
        ...prev,
        overSection: sectionId,
      };
    });
  }

  const handleAssetDragEnd = () => {
    setDraggingAsset(null);
    setDraggingSection(null);
  }


  const handleRemoveAsset = (section: number | null, asset: string) => {
    dispatch(removeAssetFromDealRoom({
      dealRoom: dealRoom.id!,
      section: section ?? undefined,
      asset: asset
    }));
  }


  const handleAssetMove = (section: SectionState | null, updatedAssets: DealRoomAsset[]) => {
    dispatch(rearrageDealRoomAssets({
      id: dealRoom.id ?? "",
      sectionIndex: section?.index,
      updatedAssets: updatedAssets
    }))
  }

  const handleAssetMoveToSection = (source: SectionState, target: number, asset: DealRoomAsset) => {

    dispatch(moveAssetToSection({
      dealRoom: dealRoom.id!,
      source: source.index,
      target: target,
      asset: asset
    }));
  }


  const handleAddSectionFinish = () => {
    handleAddSectionClose();
    const index = _.size(sectionItems);
    handleSectionClick(index);
  }

  const handleAddSectionClose = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: null
    });
  }

  const handleCloseAddAsset = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: null
    });
  }

  const handleAddAssetFinish = (entities: (Asset | AssetUpload | Collection)[]) => {
    if (_.size(entities) > 0) {

      const uploading = entities.filter(item => item.entityType == 'asset-upload');
      if (!_.isEmpty(uploading)) {
        const assest = entities.filter(item => _.includes(['asset', 'asset-upload'], item.entityType));
        dispatch(startBackgroundUploadTask({
          assets: assest as [Asset | AssetUpload],
          entity: {
            id: dealRoom.id!,
            entityType: 'deal_room',
            name: dealRoom.name || "",
            url: `/deal-room/${dealRoom.id}/assets`,
            section: sectionId
          }
        }));
      } else {
        const assets = entities.filter(item => item.entityType == 'asset');
        dispatch(addAssetToDealRoom({
          dealRoom: dealRoom.id!,
          section: sectionId ? parseInt(sectionId) : undefined,
          assets: assets.map(item => ({ id: item.id, asset: item } as DealRoomAsset)),
          addAt: "end"
        }));
      }
    }

    handleCloseAddAsset();
  }

  const activeIndex = parseInt(sectionId || '0');
  const section = sectionItems[activeIndex];

  return (
    <div className={cx(styles.dynamicAssets, {
      [styles.active]: visible
    })}>
      <p className={styles.header}>Internal documents {!editing ? `(${_.get(dealRoom, "internalAssetsCount", 0)})` : ""}</p>
      <p className={styles.subText}>Click the edit icon to add your files.</p>

      <div className={styles.contentContainer}>

        <DndContext sensors={sensors}
          collisionDetection={fixCursorSnapOffset}
          modifiers={[]}
          onDragStart={handleAssetDragStart}
          onDragMove={handleAssetDragMove}
          onDragEnd={handleAssetDragEnd}
        >
          {_.size(sectionItems) > 0 && <>
            <SectionBar
              dealRoom={dealRoom}
              sections={sectionItems}
              selected={Number(sectionId)}
              onMove={handleSectionMove}
              onSelectionChange={handleSectionClick}
              overSection={draggingAsset?.overSection}
            />
            <div className={styles.sections}>
              {sectionItems.map((section, index) => {
                return <SectionContainer
                  key={section.uid}
                  dealRoom={dealRoom}
                  section={section}
                  onRemove={(section, asset) => handleRemoveAsset(section.index, asset.asset.id)}
                  onClick={handleAssetClickInSection}
                  onMove={handleAssetMove}
                  activeAssetLayout={activeAssetLayout}
                  onMoveToSection={handleAssetMoveToSection}
                />
              })}
            </div>
          </>}

          {_.size(sectionItems) === 0 &&
            <DraggableAssetsLayout
              dealRoom={dealRoom}
              visible={true}
              assets={dealRoomAssets}
              loading={assetsLoading}
              onRemove={(asset) => handleRemoveAsset(null, asset.id)}
              activeAssetLayout={activeAssetLayout}
              onClick={handleAssetClick}
              onMove={(updatedAssets) => handleAssetMove(null, updatedAssets)}
              onMoveToSection={() => { }}
              fetchAssetsForPage={fetchAssetsForPage}
            />}

          <DragOverlay modifiers={[restrictToWindowEdges]} dropAnimation={null} adjustScale={false}>
            {draggingSection && (
              <DraggingSectionCard section={draggingSection} />
            )}

            {draggingAsset && (
              <DraggingAssetCard activeAssetLayout={activeAssetLayout} asset={draggingAsset.asset} overSection={draggingAsset.overSection} />
            )}


          </DragOverlay>
        </DndContext>
      </div>


      <AddSectionDialog
        dealRoomId={dealRoom.id!}
        show={menuState.secondaryAction === 'add-section'}
        onFinish={handleAddSectionFinish}
        onClose={handleAddSectionClose}
      />

      {menuState.secondaryAction == 'add-asset' &&
        <AddAssetsDialog show={menuState.secondaryAction == 'add-asset'}
          selectableEntityTypes={['asset']}
          source={{
            type: 'dealRoom',
            entity: dealRoom as any,
          }}
          onFinish={handleAddAssetFinish}
          onClose={handleCloseAddAsset}
        />
      }

    </div>
  )
}

interface StaticAssetsRendererProps {
  header: string;
  subText: string;
  assets: assetsInitialState;
  loading: boolean;
  activeAssetLayout: AssetsLayoutEnum;
  visible: boolean;
  fetchAssetsForPage: (page: number) => void;
  canAddAsset: boolean;
}

const StaticAssetsRenderer: React.FC<StaticAssetsRendererProps> = ({ header, subText, assets, loading, activeAssetLayout, visible, fetchAssetsForPage, canAddAsset }) => {

  const { type } = useParams();
  const navigate = useNavigate();
  const { dealRoom } = useContext(DealRoomContext);

  const handleAssetClick = (asset: DealRoomAsset) => {
    let source = `deal-room/${dealRoom.id}/assets/${type}`;
    const url = `/assets/${asset.asset.id}?source=${source}`;
    navigate(url);
  }

  return (
    <div className={cx(styles.staticAssetsContainer, {
      [styles.active]: visible
    })}>

      <p className={styles.header}>{header}</p>
      <p className={styles.subText}>{subText}</p>

      <div className={styles.contentContainer}>

        <StaticAssetsLayout
          assets={assets}
          loading={loading}
          activeAssetLayout={activeAssetLayout}
          onClick={handleAssetClick}
          fetchAssetsForPage={fetchAssetsForPage}
          canAddAsset={canAddAsset}
        />
      </div>
    </div>
  )
}

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

export const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      height: active ? "auto" : 0,
      opacity: active ? 1 : 0,
      top: active ? 0 : 100,
      config: {
        duration: 200,
        friction: 7,
        tension: 120,
      }
    },
    [active],
  );

  return (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {children}
    </animated.div>
  );
};

export default AssetsPage